import logo from './logo.svg';
import './App.css';
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import HomePage from './pages/HomePage';
import CustomSpace from './pages/CustomSpace';
import BrowseSpace from './pages/BrowseSpace';
import GameStepPage from './pages/GameStepPage';
import GameManagementPage from './pages/GameManagementPage';
//import RemotionStudioPage from './pages/RemotionStudioPage';
import SpaceViewPage from './pages/SpaceViewPage';
import EmbedPage from './pages/EmbedPage';
import ExploreSpacesPage from './pages/ExploreSpacesPage';
import AutoPlayPage from './pages/AutoPlayPage';
import AdminPage from './pages/AdminPage';
import ChibiPreviewPage from './pages/ChibiPreviewPage';
import ExplorerTheaterPage from './pages/ExplorerTheaterPage';
import ExplorerReportPage from './pages/ExplorerReportPage';
// AI Marketplace — dev only for now
import AgentImportPage from './pages/AgentImportPage';
import AgentDashboardPage from './pages/AgentDashboardPage';
import AgentQuickstartPage from './pages/AgentQuickstartPage';
import AgentDocsPage from './pages/AgentDocsPage';
import AgentProfilePage from './pages/AgentProfilePage';
import MarketplacePage from './pages/MarketplacePage';
import WhatsNewPage from './pages/WhatsNewPage';
import LofiRadio from './components/LofiRadio';
import CaptureTray from './components/CaptureTray';
import SettleInGuide from './components/SettleInGuide';
import BugReportModal from './components/BugReportModal';
// 🔐 accounts (09-30): the sign-in sheet + toast layer and /account. Dark behind
// SHOW_SIGN_IN (src/auth/signInSwitch.js): while off they render nothing and call nothing.
import AccountLayer from './auth/AccountLayer';
import AccountPage from './pages/AccountPage';
// 🔌📱 10-02 connector: /connect, the consent page claude.ai opens before its Claude joins a room.
// Dark until the backend's MCP_CONNECTOR_ENABLED opens it (the page asks the server; a browser holding the /admin key previews it).
import ConnectPage from './pages/ConnectPage';
import HouseRedirect from './components/HouseRedirect';
import { isDemoMode } from './config/demo';
import { initAnalytics, analytics } from './utils/analytics';

// Initialize PostHog analytics on app load
initAnalytics();

const isDev = process.env.NODE_ENV === 'development';

// 📡 Admin-only, and lazy ON PURPOSE. Every other page here is an eager import,
// so adding this one the normal way would ship an admin surface inside the
// bundle every single visitor downloads. Nobody but us ever opens this route,
// so it loads on demand instead.
const AdminActivityPage = React.lazy(() => import('./pages/AdminActivityPage'));

function AppContent() {
  const location = useLocation();
  const isEmbedPage = location.pathname.startsWith('/embed');
  // The explorer theater is a full-screen show with its own audio story
  // (pre-show theme → live stream) — the global LofiRadio FAB there is
  // redundant AND its fixed top-right circle sat on the theater's SHARE
  // button (user report 07-13).
  const isTheaterPage = location.pathname.startsWith('/explorer/theater');
  // 🔌📱 10-02 connector: the consent page is bare on purpose. No floating music button (a purple circle at
  // z-index 100010), capture chip or sheets over the buttons that grant access: the embed + theater treatment.
  const isConnectPage = /^\/connect\/?$/.test(location.pathname);

  // Track SPA page navigations in Google Analytics + PostHog
  useEffect(() => {
    // 🔌📱 10-02 connector: the consent page's URL carries the OAuth request (state, PKCE challenge, redirect), so
    // it is never reported, with or without its query (REVIEW_CONNECTOR L16; GA and PostHog are also kept off it in
    // public/index.html and utils/analytics.js).
    if (isConnectPage) return;
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
      });
    }
    analytics.pageView(location.pathname + location.search);
  }, [location, isConnectPage]);

  return (
    <>
      <Routes>
        {/* In demo mode, redirect home to explore page */}
        <Route path="/" element={isDemoMode ? <Navigate to="/explore" replace /> : <HomePage />} />
        <Route path="/custom-space" element={<CustomSpace />} />
        {/* 🧱 The orphaned 4-step wizard (every seat Sonnet 4.5) retired 09-29:
            the one build form lives at /custom-space (any query rides along). */}
        <Route path="/build-space" element={<Navigate to={{ pathname: '/custom-space', search: location.search }} replace />} />
        <Route path="/browse-space" element={<BrowseSpace />} />
        <Route path="/explore" element={<ExploreSpacesPage />} />
        <Route path="/whats-new" element={<WhatsNewPage />} />
        {/* 🔐 accounts (09-30): your spaces, your AIs, where you're signed in. */}
        <Route path="/account" element={<AccountPage />} />
        {/* 🔌📱 10-02 connector: the OAuth consent page (CLAUDE_AI_CONNECTOR_SPEC_2026_10_02.md §2.2). */}
        <Route path="/connect" element={<ConnectPage />} />
        <Route path="/play" element={<AutoPlayPage />} />
        <Route path="/game-step" element={<GameStepPage />} />
        {/* myspaice.ai/house: the short link Reddit posts + the house calendar use (utils/houseLink.js) */}
        <Route path="/house" element={<HouseRedirect />} />
        <Route path="/games" element={<GameManagementPage />} />
        {/* <Route path="/remotion-studio" element={<RemotionStudioPage />} /> */}
        {/* Shareable space URLs */}
        <Route path="/space/:gameId" element={<SpaceViewPage />} />
        <Route path="/s/:gameId" element={<SpaceViewPage />} />
        {/* Embed routes for iframes */}
        <Route path="/embed/:gameId" element={<EmbedPage />} />
        {/* Admin page - dev only */}
        {/* AI Marketplace — public */}
        {/* REVERT: wrap with `{isDev && ...}` to gate to dev only */}
        <Route path="/marketplace" element={<MarketplacePage />} />
        <Route path="/agents/import" element={<AgentImportPage />} />
        <Route path="/agents/dashboard" element={<AgentDashboardPage />} />
        <Route path="/agents/quickstart" element={<AgentQuickstartPage />} />
        <Route path="/agents/docs" element={<AgentDocsPage />} />
        {/* Public agent profile (port spec §4.5 trophy shelf; Nina 08-12 ③:
            real shareable route). Static /agents/* segments above outrank
            the param route under React Router v6 ranking. */}
        <Route path="/agents/:id" element={<AgentProfilePage />} />
        {/* Admin is key-gated inside the page (admin_key param on every call), so
            the route ships in prod builds too — Nina needs /admin on the
            deployed dev site (07-13). REVERT: re-wrap with {isDev && ...}. */}
        <Route path="/admin" element={<AdminPage />} />
        {/* Lazy — needs its own Suspense boundary, the app has no global one. */}
        <Route path="/admin/activity" element={
          <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#0b0f16', color: '#8b98a9', padding: 32, fontFamily: 'system-ui, sans-serif' }}>Loading activity…</div>}>
            <AdminActivityPage />
          </React.Suspense>
        } />
        {/* Chibi 2.0 art-review page — dev-only, see chibi-rig.js */}
        {isDev && <Route path="/chibi-preview" element={<ChibiPreviewPage />} />}
        {/* Instant explorer theater — curtains from the moment a session
            starts, seamless handoff to the real viewer (07-12). */}
        <Route path="/explorer/theater" element={<ExplorerTheaterPage />} />
        {/* Expedition report — full report card + field notes for one
            session; the rail card's grade stamps deep-link here (07-16). */}
        <Route path="/explorer/report/:session" element={<ExplorerReportPage />} />
        {/* Catch-all: an unknown URL rendered a blank white page (no route
            matched, nothing painted). Send strays to the explore feed. */}
        <Route path="*" element={<Navigate to="/explore" replace />} />
      </Routes>
      {!isEmbedPage && !isTheaterPage && !isConnectPage && <LofiRadio />}
      {/* 🎯 Capture tray (marketplace backlog §4.6): renders nothing until
          this browser has captured agents. Bottom-left; LofiRadio owns
          top-right. */}
      {!isEmbedPage && !isTheaterPage && !isConnectPage && <CaptureTray />}
      {!isEmbedPage && !isTheaterPage && !isConnectPage && <SettleInGuide />}
      {/* 🐛 Bug report modal (09-20): renders nothing until openBugReport()
          fires (`/bug` in the composer, the What's New pill's button). */}
      {!isEmbedPage && !isTheaterPage && !isConnectPage && <BugReportModal />}
      {/* 🔐 accounts (09-30): renders nothing until openSignIn() / showAccountToast(). */}
      {!isEmbedPage && !isTheaterPage && !isConnectPage && <AccountLayer />}
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
